import {
  alpha,
  Backdrop,
  Box,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import NoCustomerDataImage from "assets/images/lookingForData.svg";
import { CircularLoader, CustomHelmet } from "components";
import { TrialPeriodBanner } from "components/banner/TrialPeriodBanner";
import { useGlobalStore, usePermissionStore } from "controller";
import { PERMISSIONS } from "shared";
import { useAiHomeScreenView, useAiHomeSessionTracker } from "./analytics";
import { CalendarSection } from "./calendar-section";
import { HomeAiChat } from "./home-ai-chat";
import { useHomeViewMode } from "./hooks/useHomeViewMode";
import { HomeDashboard } from "./main-legacy/HomeDashboard";

/**
 * Everything below the permission gate lives here so that a resource without
 * ticket access never mounts it — no preference request, no dashboard or
 * calendar queries, just the no-access overlay.
 */
const HomeScreenContent = () => {
  const theme = useTheme();
  const { mode, isResolving } = useHomeViewMode();

  useAiHomeScreenView({ mode, isResolving });
  useAiHomeSessionTracker(mode);

  return (
    <Box
      sx={{
        display: "grid",
        // Calendar rail grows in steps with the viewport so it keeps a sane
        // share of the frame (~24% at 1280px down to ~11% at 3840px).
        gridTemplateColumns: "1fr 304px",
        "@media (min-width: 1600px)": { gridTemplateColumns: "1fr 320px" },
        "@media (min-width: 1920px)": { gridTemplateColumns: "1fr 360px" },
        "@media (min-width: 2560px)": { gridTemplateColumns: "1fr 400px" },
        "@media (min-width: 3840px)": { gridTemplateColumns: "1fr 440px" },
        gridTemplateRows: "minmax(0, 1fr)",
        backgroundColor: theme.palette.neutral["010"],
        flex: 1,
        minHeight: 0,
        width: "100%",
        overflow: "hidden",
        p: "8px",
        gap: "8px",
      }}
    >
      <Box
        sx={{
          width: "100%",
          height: "100%",
          minHeight: 0,
          overflow: "hidden",
          backgroundColor: theme.palette.neutral["010"],
        }}
      >
        {isResolving ? (
          // Same white card the dashboard renders, so the view-mode resolution
          // doesn't flash a differently shaped placeholder before it lands.
          <Stack
            sx={{
              width: "100%",
              height: "100%",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "8px",
              backgroundColor: theme.palette.neutral.main,
            }}
          >
            <CircularLoader />
          </Stack>
        ) : mode === "legacy" ? (
          <HomeDashboard />
        ) : (
          // <HomeDashboard />
          <HomeAiChat />
        )}
      </Box>
      <Box
        sx={{
          height: "100%",
          minHeight: 0,
        }}
      >
        <CalendarSection />
      </Box>
    </Box>
  );
};

const HomeScreen = () => {
  const theme = useTheme();
  const subscriptionStatus = useGlobalStore(
    (state) => state.subscriptionStatus,
  );
  // Same gate as the old home screen: everything on this page is ticket data,
  // so a resource with neither ticket scope gets the no-access overlay instead
  // of an empty dashboard.
  const havePermissionToViewHomeScreen = usePermissionStore((state) =>
    state.hasPermissions(
      [PERMISSIONS.SD.SD_TICKET_ALL_RW, PERMISSIONS.SD.SD_TICKET_ASSIGNED_RW],
      "OR",
    ),
  );

  return (
    <>
      <CustomHelmet title="Home" />
      <Box
        sx={{
          height: "calc(100vh - var(--topbar-height))",
          position: "relative",
          display: "flex",
          flexDirection: "column",
          backgroundColor: theme.palette.neutral["010"],
        }}
      >
        {subscriptionStatus !== "active" && <TrialPeriodBanner />}
        <Backdrop
          open={!havePermissionToViewHomeScreen}
          sx={{
            color: theme.palette.neutral.main,
            zIndex: 10,
            backdropFilter: "blur(15px)",
            backgroundColor: alpha(theme.palette.neutral.main, 0.1),
            width: "100%",
            height: "100%",
            position: "absolute",
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            "&>img": {
              width: "100%",
              height: "auto",
              aspectRatio: "1/1",
              maxWidth: "240px",
            },
          }}
        >
          <Stack
            sx={{
              alignItems: "center",
              justifyContent: "center",
              gap: "24px",
              position: "sticky",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
            }}
          >
            <img alt="no access to tickets" src={NoCustomerDataImage} />
            <Typography variant="h4" textAlign="center">
              It’s Seems like you don't have
              <br />
              access to Tickets!
            </Typography>
          </Stack>
        </Backdrop>
        {havePermissionToViewHomeScreen ? <HomeScreenContent /> : null}
      </Box>
    </>
  );
};

export default HomeScreen;
